import { makeAutoObservable } from 'mobx'
import { type IAPiOption } from 'shared/api'
import { type IDropdownItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { ComplianceApi, type IResponseComplianceServicesBrand } from 'entities/Compliance'
import { ReachableUrlStore } from 'entities/Compliance/ui/ReachableUrl'

export class BusinessInfoStore {
  private _businessTypes: IAPiOption[] = []
  private _businessIndustries: IAPiOption[] = []
  private _activeBusinessTypeId: number | null = null
  private _activeBusinessIndustryId: number | null = null
  private _websiteUrl: string | null = null
  private _socialMediaUrl: string | null = null
  private _isCorrectUrl = false
  private _noWebsiteUrl = false
  private _regionsOfOperation: IAPiOption[] = []
  private _activeRegionOfOperationIds: number[] = []
  private _needCheckUrlAfterChange = false

  readonly reachableWebsiteUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })
  readonly reachableSocialUrlStore = new ReachableUrlStore({
    errorText: 'Please enter a valid url (e.g. https://www.example.com/)',
    warningText:
      "Couldn't verify this link automatically. You can still continue — make sure it opens publicly.",
  })

  constructor() {
    makeAutoObservable(this)

    this.init()
  }

  get businessTypes(): IDropdownItem[] {
    return this._businessTypes.map((item) => ({ label: item.name || '', id: item.id }))
  }

  get activeBusinessTypeId() {
    return this._activeBusinessTypeId
  }

  get businessIndustries(): IDropdownItem[] {
    return this._businessIndustries.map((item) => ({ label: item.name || '', id: item.id }))
  }

  get activeBusinessIndustryId() {
    return this._activeBusinessIndustryId
  }

  get websiteUrl() {
    return this._websiteUrl
  }

  get isCorrectUrl() {
    return this._isCorrectUrl
  }

  get socialMediaUrl() {
    return this._socialMediaUrl
  }

  get noWebsiteUrl() {
    return this._noWebsiteUrl
  }

  get activeRegionOfOperationIds() {
    return this._activeRegionOfOperationIds
  }

  get regionsOfOperation(): IDropdownItem[] {
    return this._regionsOfOperation.map((item) => ({ label: item.name || '', id: item.id }))
  }

  get activeRegionsOfOperation(): IDropdownItem[] {
    return this._regionsOfOperation
      .filter((item) => this.activeRegionOfOperationIds.includes(item.id as number))
      .map((item) => ({ label: item.name || '', id: item.id }))
  }

  get isNextDisabled() {
    const socialMediaUrlRequired = this._noWebsiteUrl && !this._socialMediaUrl
    const noRequiredWebsiteUrl = !this._noWebsiteUrl && !this._websiteUrl

    if (
      this.reachableWebsiteUrlStore.isError ||
      this.reachableSocialUrlStore.isError ||
      !this._activeBusinessTypeId ||
      !this._activeBusinessIndustryId ||
      socialMediaUrlRequired ||
      noRequiredWebsiteUrl ||
      !this._activeRegionOfOperationIds?.length ||
      this._needCheckUrlAfterChange
    ) {
      return true
    }

    return false
  }

  init = () => {
    this.loadBusinessTypes()
    this.loadBusinessIndustries()
    this.loadBusinessProfileRegionsOfOperation()
  }

  initFromBrand = (data: IResponseComplianceServicesBrand['data']) => {
    if (!data) return

    this.setBusinessType(data.business_type?.id || null)
    this.setBusinessIndustry(data.industry?.id || null)
    this.setWebsiteUrl(data.website_url, true)
    this.setSocialMediaUrl(data.socialMediaProfile)
    this.setNoWebsiteUrl(Boolean(data.socialMediaProfile))
    data.regions_of_operation &&
      this.setActiveRegionOfOperationIds(data.regions_of_operation.map(({ id }) => id))
  }

  checkUrl = async (url: string, type: 'websiteUrl' | 'socialMediaUrl') => {
    const result =
      type === 'websiteUrl'
        ? await this.reachableWebsiteUrlStore.checkSingleUrl(url)
        : await this.reachableSocialUrlStore.checkSingleUrl(url)

    if (!result.isSuccess && type === 'websiteUrl' && this._noWebsiteUrl) {
      this.reachableWebsiteUrlStore.dispose()
      return
    }

    if (result.isWarning) {
      this._isCorrectUrl = false
      this._needCheckUrlAfterChange = false
      return
    }

    if (result.isError) {
      this._isCorrectUrl = false
      this._needCheckUrlAfterChange = true
      return
    }

    this._isCorrectUrl = true
    this._needCheckUrlAfterChange = false
  }

  setBusinessType = (businessTypeId: number | null) => {
    this._activeBusinessTypeId = businessTypeId
  }

  loadBusinessTypes = async () => {
    if (this._businessTypes.length) return

    try {
      const { data } = await ComplianceApi.getBusinessProfileBusinessTypes()
      this._businessTypes = data
    } catch (e) {
      logger.error(e)
    }
  }

  setBusinessIndustry = (businessIndustryId: number | null) => {
    this._activeBusinessIndustryId = businessIndustryId
  }

  loadBusinessIndustries = async () => {
    if (this._businessIndustries.length) return

    try {
      const { data } = await ComplianceApi.getBusinessProfileIndustries()
      this._businessIndustries = data
    } catch (e) {
      logger.error(e)
    }
  }

  setIsCorrectUrl = (value: boolean) => {
    this._isCorrectUrl = value
  }

  setWebsiteUrl = (url: string | null, fromRequest?: boolean) => {
    this._needCheckUrlAfterChange = !fromRequest
    this.setIsCorrectUrl(false)

    this.reachableWebsiteUrlStore.dispose()

    if (fromRequest && url?.length) {
      this.setIsCorrectUrl(true)
    }

    this._websiteUrl = url
  }

  setSocialMediaUrl = (url: string | null) => {
    this.reachableSocialUrlStore.dispose()
    this._socialMediaUrl = url
  }

  setNoWebsiteUrl = (value: boolean) => {
    this._noWebsiteUrl = value
  }

  handleSetNoWebsiteUrl = () => {
    this.reachableSocialUrlStore.dispose()
    this.reachableWebsiteUrlStore.dispose()

    this.setWebsiteUrl(null)
    this.setSocialMediaUrl(null)
    this.setNoWebsiteUrl(!this._noWebsiteUrl)
  }

  setRegionsOfOperationId = (regionsOfOperation: number) => {
    this._activeRegionOfOperationIds = [...this.activeRegionOfOperationIds, regionsOfOperation]
  }

  setActiveRegionOfOperationIds = (ids: number[]) => {
    this._activeRegionOfOperationIds = ids
  }

  deleteRegionsOfOperationId = (regionsOfOperation: number) => {
    this._activeRegionOfOperationIds = this.activeRegionOfOperationIds.filter(
      (id) => id !== regionsOfOperation
    )
  }

  loadBusinessProfileRegionsOfOperation = async () => {
    if (this._regionsOfOperation.length) return

    try {
      const { data } = await ComplianceApi.getBusinessProfileRegionsOfOperation()
      this._regionsOfOperation = data
    } catch (e) {
      logger.error(e)
    }
  }
}
